import { useCallback, useEffect, useState } from 'react';

/**
 * 현재 브라우저 hash를 안전한 문자열로 정규화한다.
 */
export function normalizeBrowserServiceHash(locationLike) {
  return String(locationLike?.hash || '').trim();
}

/**
 * App의 authenticated service hash 구독과 소비를 관리한다.
 * replaceState는 hashchange를 발생시키지 않으므로 state도 함께 갱신한다.
 */
export default function useServiceHash() {
  const [routeLocation, setRouteLocation] = useState(() => {
    if (typeof window === 'undefined') return { hash: '', pathname: '/' };
    return {
      hash: normalizeBrowserServiceHash(window.location),
      pathname: window.location.pathname,
    };
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const syncRouteLocation = () => {
      const next = {
        hash: normalizeBrowserServiceHash(window.location),
        pathname: window.location.pathname,
      };
      setRouteLocation((current) => (
        current.hash === next.hash && current.pathname === next.pathname
          ? current
          : next
      ));
    };
    syncRouteLocation();
    window.addEventListener('hashchange', syncRouteLocation);
    window.addEventListener('popstate', syncRouteLocation);
    return () => {
      window.removeEventListener('hashchange', syncRouteLocation);
      window.removeEventListener('popstate', syncRouteLocation);
    };
  }, []);

  const consumeRouteHash = useCallback((nextHash = '') => {
    const normalizedNextHash = normalizeBrowserServiceHash({ hash: nextHash });
    if (typeof window !== 'undefined') {
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${window.location.search}${normalizedNextHash}`,
      );
    }
    setRouteLocation((current) => ({ ...current, hash: normalizedNextHash }));
  }, []);

  return {
    routeHash: routeLocation.hash,
    routePathname: routeLocation.pathname,
    consumeRouteHash,
  };
}
